import React from 'react'

import Feather from 'react-native-vector-icons/Feather'
import { ThemeProvider, Theme, createTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useParams } from 'react-router-dom'

import * as c from '../../../../common'
import * as r from '../../../../react-utils'

import * as cp from '../../../comps'
import * as cpm from '../../../../app/components'
import * as gs from '../../../../app/gStyles'

import { columnWidths, fontFamily, fontSize } from './common'

export type DetailLineProps = r.DetailLine &
  c.r<{
    collapsed: boolean
    isPDF: boolean
    /**
     * V43-style gate: must be true during public token signing (?mode=sign).
     * The pencil icon opens a live <CheckGroup> editor (qty/notes/cost/
     * customer-cost, add/remove rows) writing straight to Firebase via the
     * same component reps use - never expose it to a signing customer.
     */
    readOnly?: boolean
    onCollapse(): void
    onExpand(): void
  }>

export default React.memo<DetailLineProps>(function DetailLine({
  collapsed,
  id,
  isPDF,
  name,
  notes,
  onCollapse,
  onExpand,
  price,
  qty,
  readOnly,
}) {
  //#region styling
  const nativePrefersDark = useMediaQuery('(prefers-color-scheme: dark)')
  const innerTheme = React.useMemo(
    (): Theme =>
      createTheme({
        palette: { mode: nativePrefersDark ? 'dark' : 'light' },
        breakpoints: { values: r.MUIBreakpoints },
      }),
    [nativePrefersDark],
  )
  //#endregion styling
  //#region global
  const { customerID } = useParams<'customerID'>()
  if (!customerID) {
    throw new ReferenceError(
      `customerID route param not found in <NewJobSummary />, instead found: ${customerID}`,
    )
  }
  //#endregion global
  //#region local
  const [editingIs, , editingToggle] = r.useBool(false)
  //#endregion
  const showNotes = (isPDF || !collapsed) && notes.length > 0
  const mainNode = [
    <div
      key={id}
      style={{
        ...gs.flowRootWidth100,
        margin: 0,
        marginBottom: showNotes ? 0 : 12,
        width: '100%',
      }}
    >
      {!isPDF && (
        <div
          style={{
            ...gs.rowWrap,
            float: 'left',
            width: columnWidths[0],
          }}
        >
          <Feather
            color={
              c.efficiencyToInputs[id as c.CustomerField] && !readOnly
                ? iconColor
                : 'transparent'
            }
            name="edit-2"
            onPress={
              c.efficiencyToInputs[id as c.CustomerField] && !readOnly
                ? editingToggle
                : undefined
            }
            size={iconSize}
            style={iconStyle}
          />

          <Feather
            color={
              c.efficiencyToInputs[id as c.CustomerField]
                ? notes.length === 0 && !id.startsWith('security_')
                  ? nearWhite
                  : iconColor
                : 'transparent'
            }
            name={
              collapsed || (notes.length === 0 && !id.startsWith('security_'))
                ? 'maximize-2'
                : 'minimize-2'
            }
            onPress={
              c.efficiencyToInputs[id as c.CustomerField]
                ? collapsed
                  ? onExpand
                  : onCollapse
                : undefined
            }
            size={iconSize}
            style={iconStyle}
          />
        </div>
      )}

      <p
        style={{
          color: '#000000',
          float: 'left',
          fontFamily,
          fontSize,
          // Jaron, 12993381781: Qty/Description should read bold like the
          // header above them (DetailHeader.tsx) — they didn't.
          fontWeight: 'bold',
          margin: 0,
          textAlign: 'center',
          width: columnWidths[1],
        }}
      >
        {qty === -1 ? String.fromCharCode(160) : qty}
      </p>

      <div
        style={{
          ...gs.column,
          margin: 0,
          float: 'left',
          fontFamily,
          fontSize,
          width: isPDF
            ? parseFloat(columnWidths[0]) + parseFloat(columnWidths[2]) + '%'
            : columnWidths[2],
        }}
      >
        <p
          style={{
            color: '#000000',
            float: 'left',
            fontFamily,
            fontSize,
            // Jaron, 12993381781: Qty/Description should read bold like the
            // header above them (DetailHeader.tsx) — they didn't.
            fontWeight: 'bold',
            margin: 0,
          }}
        >
          {name}
        </p>
      </div>

      <p
        style={{
          color: '#000000',
          margin: 0,
          float: 'left',
          fontFamily,
          fontSize,
          textAlign: 'end',
          width: columnWidths[3],
        }}
      >
        {price === -1 || qty === -1
          ? '-'
          : Intl.NumberFormat('en-US', {
              currency: 'USD',
              maximumFractionDigits: 2,
              minimumFractionDigits: 2,
              style: 'currency',
            }).format(price)}
      </p>

      <p
        style={{
          color: '#000000',
          float: 'left',
          fontFamily,
          fontSize,
          margin: 0,
          textAlign: 'end',
          width: columnWidths[4],
        }}
      >
        {price === -1
          ? '-'
          : Intl.NumberFormat('en-US', {
              currency: 'USD',
              maximumFractionDigits: 2,
              minimumFractionDigits: 2,
              style: 'currency',
              // Math.abs() in case qty is -1 (flag for non-quantifiable)
            }).format(price * Math.abs(qty))}
      </p>
    </div>,
    ...(showNotes
      ? notes.map((noteLine, i) => (
          <p
            key={`${id}-note-${i}`}
            style={{
              alignSelf: 'flex-start',
              margin: 0,
              marginTop: i === 0 ? 2 : 0,
              marginBottom: i === notes.length - 1 ? 12 : 0,
              marginLeft: isPDF
                ? `calc(${columnWidths[1]} + 3ch)`
                : `calc(${columnWidths[0]} + ${columnWidths[1]} + 3ch)`,
              width: isPDF
                ? `calc(${
                    parseFloat(columnWidths[0]) + parseFloat(columnWidths[2])
                  }% - 3ch)`
                : `calc(${columnWidths[2]} - 3ch)`,
              fontFamily,
              fontSize,
            }}
          >
            {noteLine}
          </p>
        ))
      : []),
  ]

  return (
    <>
      {mainNode}

      <ThemeProvider theme={innerTheme}>
        <cp.Dialog
          noCanvas
          onClose={editingToggle}
          open={editingIs && !readOnly}
        >
          {editingIs && !readOnly && (
            <cpm.CheckGroup
              alwaysOn
              as="canvas"
              customerID={customerID}
              field={id as c.CustomerField}
            />
          )}
        </cp.Dialog>
      </ThemeProvider>
    </>
  )
})

export const iconSize = 14
export const iconStyle = { ...gs.userSelectNone, margin: 4 }

const nearWhite = '#DDDDDD'

const iconColor = '#777777'
